<template>
  <div class="required-page">
    <div class="required-page__header">
      <h2 class="required-page__title">Мои потребности в материалах</h2>
      <q-btn
        color="primary"
        label="Создать потребность"
        icon="add"
        no-caps
        @click="onCreateClick"
      />
    </div>

    <CustomTable
      :rowsData="rows"
      :columns="columns"
      :loading="isLoading"
      :show-search="true"
      :custom-body="true"
      :update-data="loadData"
    >
      <template #customBody="props">
        <q-tr
          :props="props"
          class="cursor-pointer"
          @click="onRowClick(props.row)"
        >
          <custom-table-cell align="left">
            {{ props.row.sbs_project_name || '-' }}
          </custom-table-cell>
          <custom-table-cell align="left">
            {{ props.row.required_type_name || '-' }}
          </custom-table-cell>
          <custom-table-cell align="left">
            {{ props.row.author_name || '-' }}
          </custom-table-cell>
          <custom-table-cell align="left">
            {{ props.row.executor_name || '-' }}
          </custom-table-cell>
          <custom-table-cell align="left">
            {{ props.row.sklad_name || '-' }}
          </custom-table-cell>
          <custom-table-cell align="center">
            <q-icon
              :name="props.row.is_level ? 'check_circle' : 'cancel'"
              :color="props.row.is_level ? 'positive' : 'grey-5'"
              size="sm"
            />
          </custom-table-cell>
          <custom-table-cell align="center">
            {{ props.row.date_begin || '-' }}
          </custom-table-cell>
          <custom-table-cell align="center">
            {{ props.row.date_end || '-' }}
          </custom-table-cell>
          <custom-table-cell align="center">
            <div v-if="progressMap[props.row.required_id]" class="rq-mini-progress">
              <div class="rq-mini-bar">
                <div class="rq-mini-seg rq-mini--required" :style="{ width: miniPct(props.row.required_id, 'required') }"></div>
                <div class="rq-mini-seg rq-mini--ordered" :style="{ width: miniPct(props.row.required_id, 'ordered') }"></div>
                <div class="rq-mini-seg rq-mini--delivered" :style="{ width: miniPct(props.row.required_id, 'delivered') }"></div>
              </div>
              <q-tooltip>
                <div class="rq-tip-row"><span class="rq-tip-dot" style="background:#ff9800"></span> Потребность: {{ fmtNum(progressMap[props.row.required_id].required) }} ({{ miniPctNum(props.row.required_id, 'required') }}%)</div>
                <div class="rq-tip-row"><span class="rq-tip-dot" style="background:#2196f3"></span> Заказано: {{ fmtNum(progressMap[props.row.required_id].ordered) }} ({{ miniPctNum(props.row.required_id, 'ordered') }}%)</div>
                <div class="rq-tip-row"><span class="rq-tip-dot" style="background:#4caf50"></span> Доставлено: {{ fmtNum(progressMap[props.row.required_id].delivered) }} ({{ miniPctNum(props.row.required_id, 'delivered') }}%)</div>
                <div class="rq-tip-row"><span class="rq-tip-dot" style="background:#999"></span> Смета: {{ fmtNum(progressMap[props.row.required_id].smeta) }}</div>
              </q-tooltip>
            </div>
            <span v-else class="text-grey-5">—</span>
          </custom-table-cell>
          <custom-table-cell align="center">
            <span class="rq-action-wrap">
              <q-btn
                flat dense round
                icon="edit"
                size="sm"
                :color="props.row.can_edit ? 'primary' : 'grey-5'"
                :disable="!props.row.can_edit"
                @click.stop="onEditClick(props.row)"
              />
              <q-tooltip v-if="!props.row.can_edit">Редактировать может только автор или делегат с правом редактирования</q-tooltip>
            </span>
            <span class="rq-action-wrap">
              <q-btn
                flat dense round
                icon="delete_outline"
                size="sm"
                :color="props.row.can_edit ? 'negative' : 'grey-5'"
                :disable="!props.row.can_edit"
                @click.stop="onDeleteClick(props.row)"
              />
              <q-tooltip v-if="!props.row.can_edit">Редактировать может только автор или делегат с правом редактирования</q-tooltip>
            </span>
          </custom-table-cell>
        </q-tr>
      </template>
    </CustomTable>

    <q-dialog v-model="dialog" persistent>
      <q-card style="min-width: 480px;">
        <q-card-section class="row items-center q-pb-none">
          <div class="text-h6">{{ form.required_id ? 'Редактировать' : 'Создать' }} потребность</div>
          <q-space />
          <q-btn icon="close" flat round dense v-close-popup />
        </q-card-section>

        <q-card-section>
          <div v-if="isLoadingOptions" class="column items-center q-pa-lg">
            <q-spinner color="primary" size="40px" />
            <div class="q-mt-sm">Загрузка данных...</div>
          </div>

          <q-form v-else @submit.prevent="onSave" class="q-gutter-md">
            <CustomSelect
              label="Проект"
              :options="projects"
              option-value-key="sbs_project_id"
              option-label-key="sbs_project_name"
              :value="form.sbs_project_id"
              @change="onProjectChange"
              :required="true"
              :rules="[val => !!val || 'Выберите проект']"
              :disabled="projects.length === 1"
            />

            <div class="custom-select__wrapper">
              <div class="custom-select__label">Тип потребности<span style="color: red;">*</span></div>
              <q-select
                v-model="selectedRequiredType"
                :options="filteredRequiredTypes"
                option-value="required_type_id"
                option-label="required_type_name"
                dense
                outlined
                use-input
                map-options
                options-dense
                :placeholder="form.sbs_project_id ? 'Выберите..' : 'Сначала выберите проект'"
                input-debounce="0"
                @filter="onFilterRequiredTypes"
                :rules="[val => !!val || 'Выберите тип потребности']"
                hide-bottom-space
                class="custom-select"
                :disable="!form.sbs_project_id"
              >
                <template #option="{ itemProps, opt }">
                  <q-item
                    v-bind="itemProps"
                    dense
                    :disable="!!opt.is_used"
                    :class="{ 'rq-option--used': !!opt.is_used }"
                  >
                    <q-item-section>
                      <q-item-label style="font-size:13px">{{ opt.required_type_name }}</q-item-label>
                    </q-item-section>
                    <q-item-section side v-if="opt.is_used">
                      <q-icon name="check_circle" color="positive" size="xs" />
                    </q-item-section>
                    <q-tooltip v-if="opt.is_used">Уже есть в потребностях</q-tooltip>
                  </q-item>
                </template>
                <template #no-option>
                  <q-item>
                    <q-item-section class="text-grey text-center" style="font-size:13px">Не найдено</q-item-section>
                  </q-item>
                </template>
              </q-select>
            </div>

            <CustomSelect
              label="Снабженец"
              :options="executors"
              option-value-key="executor_id"
              option-label-key="executor_fio"
              :value="form.executor_id"
              @change="(val) => form.executor_id = val"
              :loading="isLoadingExecutors"
              :disabled="!form.required_type_id"
              :placeholder="form.required_type_id ? 'Выберите снабженца...' : 'Сначала выберите тип потребности'"
            />

            <CustomSelect
              label="Склад"
              :options="skladOptions"
              option-value-key="sbs_sklad_id"
              option-label-key="naimenovanie"
              :value="form.sbs_sklad_id"
              @change="(val) => form.sbs_sklad_id = val"
              :loading="isLoadingSklads"
              :disabled="!form.sbs_project_id"
              :placeholder="form.sbs_project_id ? 'Выберите склад...' : 'Сначала выберите проект'"
            />

            <q-checkbox
              v-model="form.is_level"
              label="Учитывает этажи"
            />

            <CustomDate
              label="Дата начала"
              :value="form.date_begin"
              @change_date="(val) => form.date_begin = val"
              :required="true"
              fullWidth
            />

            <CustomDate
              label="Дата окончания"
              :value="form.date_end"
              @change_date="(val) => form.date_end = val"
              :required="true"
              :minDate="form.date_begin"
              fullWidth
            />

            <div class="row justify-end q-gutter-sm q-mt-md">
              <q-btn flat label="Отмена" color="grey" v-close-popup no-caps />
              <q-btn
                unelevated
                label="Сохранить"
                color="primary"
                type="submit"
                :loading="isSaving"
                no-caps
              />
            </div>
          </q-form>
        </q-card-section>
      </q-card>
    </q-dialog>
  </div>
</template>

<script>
import instance from "@/services/api";
import CustomTable from "@/components/common/CustomTable/index.vue";
import CustomTableCell from "@/components/common/CustomTable/custom-table-cell.vue";
import CustomSelect from "@/components/form/CustomSelect/index.vue";
import CustomDate from "@/components/form/CustomDate/index.vue";

const columns = [
  { name: "sbs_project_name", label: "Проект", align: "left", field: "sbs_project_name" },
  { name: "required_type_name", label: "Тип потребности", align: "left", field: "required_type_name" },
  { name: "author_name", label: "Автор", align: "left", field: "author_name" },
  { name: "executor_name", label: "Исполнитель", align: "left", field: "executor_name" },
  { name: "sklad_name", label: "Склад", align: "left", field: "sklad_name" },
  { name: "is_level", label: "Учитывает этажи", align: "center", field: "is_level" },
  { name: "date_begin", label: "Дата начала", align: "center", field: "date_begin" },
  { name: "date_end", label: "Дата окончания", align: "center", field: "date_end" },
  { name: "progress", label: "Прогресс", align: "center", style: "width: 140px" },
  { name: "actions", label: "", align: "center" },
];

export default {
  name: "Required",
  components: { CustomTable, CustomTableCell, CustomSelect, CustomDate },
  data() {
    return {
      rows: [],
      columns,
      isLoading: false,
      dialog: false,
      isLoadingOptions: false,
      isSaving: false,
      projects: [],
      requiredTypes: [],
      filteredRequiredTypes: [],
      progressMap: {},
      skladOptions: [],
      isLoadingSklads: false,
      executors: [],
      isLoadingExecutors: false,
      form: {
        required_id: null,
        sbs_project_id: null,
        required_type_id: null,
        executor_id: null,
        sbs_sklad_id: null,
        is_level: false,
        date_begin: null,
        date_end: null,
      },
    };
  },
  computed: {
    selectedRequiredType: {
      get() {
        if (!this.form.required_type_id) return null;
        return this.requiredTypes.find(
          t => t.required_type_id == this.form.required_type_id
        ) || null;
      },
      set(val) {
        const newId = val ? val.required_type_id : null;
        if (newId !== this.form.required_type_id) {
          this.form.required_type_id = newId;
          this.form.executor_id = null;
          this.executors = [];
          if (newId) this.loadExecutors(newId);
        }
      },
    },
  },
  mounted() {
    this.loadData();
  },
  methods: {
    fmtNum(v) {
      if (v == null || v === '') return '';
      const n = Number(v);
      if (isNaN(n)) return v;
      return parseFloat(n.toFixed(2));
    },
    miniPctNum(id, type) {
      const p = this.progressMap[id];
      if (!p || !p.smeta) return 0;
      const val = p[type] || 0;
      return parseFloat((val / p.smeta * 100).toFixed(1));
    },
    miniPct(id, type) {
      const pct = this.miniPctNum(id, type);
      if (pct <= 0) return '0';
      const minPx = { required: 6, ordered: 4, delivered: 2 };
      return `max(${pct}%, ${minPx[type] || 2}px)`;
    },
    async loadData() {
      this.isLoading = true;
      try {
        const { data } = await instance().get("/required/read/");
        this.rows = data?.data || [];
        this.loadAllProgress();
      } catch (e) {
        console.error(e);
      } finally {
        this.isLoading = false;
      }
    },
    async loadAllProgress() {
      const toNum = (v) => parseFloat(String(v).replace(',', '.')) || 0;
      const results = await Promise.allSettled(
        this.rows.map(r =>
          instance().get(`/required/${r.required_id}/progress/`).then(resp => {
            const pRows = resp.data?.data;
            if (pRows && pRows.length) {
              const p = pRows[0];
              return {
                id: r.required_id,
                smeta: toNum(p.smeta_amount),
                required: toNum(p.required_amount),
                ordered: toNum(p.ordered_amount),
                delivered: toNum(p.delivered_amount),
              };
            }
            return null;
          })
        )
      );
      const map = {};
      for (const r of results) {
        if (r.status === 'fulfilled' && r.value) {
          map[r.value.id] = r.value;
        }
      }
      this.progressMap = map;
    },
    async onCreateClick() {
      this.form = { required_id: null, sbs_project_id: null, required_type_id: null, executor_id: null, sbs_sklad_id: null, is_level: false, date_begin: null, date_end: null };
      this.executors = [];
      this.dialog = true;
      await this.loadOptions();
    },
    async onEditClick(row) {
      this.form = {
        required_id: row.required_id,
        sbs_project_id: row.sbs_project_id || null,
        required_type_id: row.required_type_id || null,
        executor_id: row.executor_id || null,
        sbs_sklad_id: row.sbs_sklad_id || null,
        is_level: !!row.is_level,
        date_begin: row.date_begin || null,
        date_end: row.date_end || null,
      };
      this.executors = [];
      this.dialog = true;
      await this.loadOptions();
      if (this.form.required_type_id) {
        await this.loadExecutors(this.form.required_type_id);
      }
    },
    async loadOptions() {
      this.isLoadingOptions = true;
      try {
        const { data: projectsRes } = await instance().get("/required/projects/");
        this.projects = projectsRes?.data || [];
        if (!this.form.sbs_project_id && this.projects.length === 1) {
          this.form.sbs_project_id = this.projects[0].sbs_project_id;
        }
        if (this.form.sbs_project_id) {
          await Promise.all([
            this.loadRequiredTypes(),
            this.loadSkladOptions(),
          ]);
        }
      } catch (e) {
        console.error(e);
      } finally {
        this.isLoadingOptions = false;
      }
    },
    async loadRequiredTypes() {
      try {
        const params = {};
        if (this.form.sbs_project_id) params.sbs_project_id = this.form.sbs_project_id;
        if (this.form.required_id) params.required_id = this.form.required_id;
        const { data } = await instance().get('/required/required-types/', { params });
        this.requiredTypes = data?.data || [];
        this.filteredRequiredTypes = [...this.requiredTypes];
      } catch (e) {
        console.error(e);
      }
    },
    async loadSkladOptions() {
      this.isLoadingSklads = true;
      try {
        const { data } = await instance().get('/material/material_sklad/read/', {
          params: { sbs_project_id: this.form.sbs_project_id },
        });
        this.skladOptions = data?.data || [];
        if (this.skladOptions.length === 1 && !this.form.sbs_sklad_id) {
          this.form.sbs_sklad_id = this.skladOptions[0].sbs_sklad_id;
        }
      } catch (e) {
        console.error(e);
      } finally {
        this.isLoadingSklads = false;
      }
    },
    async loadExecutors(required_type_id) {
      this.isLoadingExecutors = true;
      try {
        const { data } = await instance().get(`/required/${required_type_id}/executors/`);
        this.executors = data?.data || [];
      } catch (e) {
        console.error(e);
      } finally {
        this.isLoadingExecutors = false;
      }
    },
    async onProjectChange(val) {
      this.form.sbs_project_id = val;
      this.form.required_type_id = null;
      this.form.executor_id = null;
      this.form.sbs_sklad_id = null;
      this.requiredTypes = [];
      this.filteredRequiredTypes = [];
      this.skladOptions = [];
      this.executors = [];
      if (val) {
        await Promise.all([
          this.loadRequiredTypes(),
          this.loadSkladOptions(),
        ]);
      }
    },
    onFilterRequiredTypes(val, update) {
      update(() => {
        if (!val) {
          this.filteredRequiredTypes = this.requiredTypes;
        } else {
          const q = val.toLowerCase();
          this.filteredRequiredTypes = this.requiredTypes.filter(
            t => (t.required_type_name || '').toLowerCase().includes(q)
          );
        }
      });
    },
    onRowClick(row) {
      this.$router.push(`/required/${row.required_id}/items`);
    },
    async onSave() {
      this.isSaving = true;
      try {
        await instance().post("/required/upd/", {
          required_id: this.form.required_id,
          required_type_id: this.form.required_type_id,
          executor_id: this.form.executor_id,
          sbs_project_id: this.form.sbs_project_id,
          sbs_sklad_id: this.form.sbs_sklad_id,
          is_level: this.form.is_level,
          date_begin: this.form.date_begin,
          date_end: this.form.date_end,
        });
        this.dialog = false;
        await this.loadData();
      } catch (e) {
        console.error(e);
      } finally {
        this.isSaving = false;
      }
    },
    onDeleteClick(row) {
      if (!row.required_id) return;
      this.$q.dialog({
        title: 'Удалить потребность',
        message: `Удалить потребность? Все связанные данные будут удалены.`,
        cancel: { label: 'Отмена', flat: true, noCaps: true },
        ok: { label: 'Удалить', color: 'negative', noCaps: true },
        persistent: true,
      }).onOk(async () => {
        try {
          await instance().post('/required/required-del/', {
            required_id: row.required_id,
          });
          await this.loadData();
        } catch (e) {
          const msg = e.response?.data?.error || e.message || 'Ошибка удаления';
          this.$q.notify({ type: 'negative', message: msg });
        }
      });
    },
  },
};
</script>

<style scoped>
.required-page {
  padding: 16px;
}
.required-page__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}
.required-page__title {
  font-size: 20px;
  font-weight: 600;
  margin: 0;
}

.rq-mini-progress {
  display: inline-block;
  width: 100%;
  min-width: 80px;
  max-width: 120px;
}
.rq-mini-bar {
  position: relative;
  height: 10px;
  background: #e8e8e8;
  border-radius: 5px;
  overflow: hidden;
}
.rq-mini-seg {
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  border-radius: 0 5px 5px 0;
  transition: width 0.4s ease;
}
.rq-mini--required { background: #ff9800; z-index: 1; }
.rq-mini--ordered  { background: #2196f3; z-index: 2; }
.rq-mini--delivered { background: #4caf50; z-index: 3; }

.rq-tip-row {
  display: flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
  font-size: 12px;
  line-height: 1.6;
}
.rq-tip-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}
.rq-action-wrap {
  display: inline-block;
  position: relative;
}

</style>

<style>
.rq-option--used {
  opacity: 0.45;
  cursor: not-allowed !important;
  pointer-events: auto !important;
}
</style>
